// The Floor grille tool and a room's Floor grille card: a click in a sauna lays
// a slatted grille over its whole floor (green), a click on a room that has one
// takes it away (red); the card edits its figures and lists its cuts. What
// stands on the floor is the pure module's (floorGrille.ts
// `settleFloorGrilles`). docs/claude/floorGrille.md.
import type {
  FloorGrille,
  ProductInstance,
  Vec2,
} from '../../shared/types';
import {
  flashToolStatus,
  instLocked,
  invalidate,
  newId,
  pushHistory,
  renderProperties,
  state,
} from './core';
import {
  grilleCutList,
  grilleFigures,
  grilleLayout,
  grilleRegion,
  grilleTop,
  settleFloorGrilles,
  takesGrille,
  withGrilleCuts,
} from '../floorGrille';
import {
  pointInPolygon,
  toInstanceLocal,
} from '../geometry';
import { tr } from '../i18n';
import {
  checkboxRow,
  deleteRow,
  infoRow,
  numberRow,
  sectionTitle,
  selectRow,
} from './panelRows';
import { WOOD_OPTIONS } from './roomCards';
import { woodOf } from '../../shared/woods';
import { polyInnerCarved } from '../polyRoom';
import { selectOne } from '../selection';
import { type ToolAim } from '../toolAim';
import {
  type BenchHover,
  deselectAll,
} from '../main';

/** The Material row's value for a grille made of what the benches are. */
const AS_BENCHES = 'benches';

/** The sauna under a world point that can take or lose a grille: the topmost,
 *  the point on its floor. */
function grilleRoomAt(world: Vec2): ProductInstance | null {
  for (let i = state.drawing.instances.length - 1; i >= 0; i--) {
    const room = state.drawing.instances[i];
    if (!takesGrille(room) || instLocked(room)) continue;
    if (pointInPolygon(toInstanceLocal(room, world), polyInnerCarved(room))) return room;
  }
  return null;
}

/** The plan of the grille a room has, or would have (room local). */
function grillePlan(room: ProductInstance): Vec2[][] {
  const r = room.floorGrille ? room : { ...room, floorGrille: { id: '' } };
  return grilleRegion(withGrilleCuts(r, state.drawing.instances)).map((g) => g.outer);
}

/** The Floor grille tool's hover: the floor a click would cover (green), or the
 *  grille it would take away (red). */
export function floorGrilleHoverAt(aim: ToolAim): BenchHover | null {
  const room = grilleRoomAt(aim.world);
  if (!room) return null;
  const polys = grillePlan(room);
  return polys.length ? { instId: room.id, polys, kind: room.floorGrille ? 'delete' : 'add' } : null;
}

export function downFloorGrilleTool(e: MouseEvent, aim: ToolAim): boolean {
  if (state.tool !== 'floor-grille' || e.button !== 0) return false;
  const room = grilleRoomAt(aim.world);
  if (!room) return true;
  pushHistory();
  if (room.floorGrille) {
    delete room.floorGrille;
    delete room.floorGrilleCuts;
    flashToolStatus(tr('Floor grille removed.'));
  } else {
    room.floorGrille = { id: newId('fg') };
    settleFloorGrilles(state.drawing.instances);
  }
  deselectAll();
  selectOne(state, 'instance', room.id);
  state.benchAddHover = floorGrilleHoverAt(aim);
  renderProperties();
  invalidate();
  return true;
}

/** A room's Floor grille card: its figures, Turn 90°, its top over the floor,
 *  its cut list and Remove. Nothing for a room without one. */
export function appendFloorGrilleCard(body: HTMLElement, room: ProductInstance): void {
  const g = room.floorGrille;
  if (!g) return;
  const f = grilleFigures(g);
  const edit = (fn: (g: FloorGrille) => void): void => { pushHistory(); fn(g); renderProperties(); invalidate(); };
  const fig = (label: string, v: number, set: (g: FloorGrille, x: number) => void, min: number): void => {
    body.appendChild(numberRow(label, v, (x) => edit((gg) => set(gg, Math.max(min, Math.round(x)))), { commit: true }));
  };
  body.appendChild(sectionTitle('Floor grille'));
  fig('Slat width (mm)', f.slatWidth, (gg, x) => { gg.slatWidth = x; }, 10);
  fig('Slat thickness (mm)', f.slatThickness, (gg, x) => { gg.slatThickness = x; }, 5);
  fig('Gap between slats (mm)', f.slatGap, (gg, x) => { gg.slatGap = x; }, 0);
  fig('Batten width (mm)', f.battenWidth, (gg, x) => { gg.battenWidth = x; }, 10);
  fig('Batten thickness (mm)', f.battenThickness, (gg, x) => { gg.battenThickness = x; }, 5);
  fig('Battens at most apart (mm)', f.battenSpacing, (gg, x) => { gg.battenSpacing = x; }, 50);
  body.appendChild(checkboxRow('Turn 90°', !!g.turned, (v) => edit((gg) => { if (v) gg.turned = true; else delete gg.turned; })));
  // Its wood: its own, or as the benches (their wood, else their colour).
  const own = woodOf(g.wood)?.id ?? AS_BENCHES;
  body.appendChild(selectRow('Material', [[AS_BENCHES, 'As the benches'], ...WOOD_OPTIONS], own, (v) => edit((gg) => {
    if (v === AS_BENCHES || !woodOf(v)) delete gg.wood; else gg.wood = woodOf(v)!.id;
  })));
  body.appendChild(infoRow('Top over the floor', `${grilleTop(room)} mm`));
  const layout = grilleLayout(room);
  if (layout) {
    for (const row of grilleCutList(layout, f)) {
      body.appendChild(infoRow(row.kind === 'slat' ? 'Slats' : 'Battens', `${row.count} × ${row.length} × ${row.width} × ${row.thickness} mm`));
    }
  }
  body.appendChild(deleteRow(() => edit(() => { delete room.floorGrille; delete room.floorGrilleCuts; }), 'Remove the floor grille', true));
}
